/**
 * Shared query layer. One QueryClient, two hooks:
 *   useRpc  — call a Postgres function (the admin_* family)
 *   useSel  — run a supabase-js select builder
 * Both surface errors instead of silently rendering zeros.
 */
import { QueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

interface Opts {
  refetchInterval?: number;
  enabled?: boolean;
  staleTime?: number;
}

export function useRpc<T = unknown>(fn: string, args?: Record<string, unknown>, options?: Opts) {
  return useQuery<T>({
    queryKey: [fn, args ?? null],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(fn, (args ?? {}) as never);
      if (error) throw new Error(error.message);
      return data as T;
    },
    ...options,
  });
}

/** Run a supabase select. The fetcher must return the builder (it is thenable). */
export function useSel<T = unknown>(
  key: readonly unknown[],
  fetcher: () => PromiseLike<{ data: unknown; error: { message: string } | null }>,
  options?: Opts,
) {
  return useQuery<T>({
    queryKey: key as unknown[],
    queryFn: async () => {
      const { data, error } = await fetcher();
      if (error) throw new Error(error.message);
      return (data ?? []) as T;
    },
    ...options,
  });
}

/** Invalidate everything — the global refresh button. */
export function refreshAll() {
  return queryClient.invalidateQueries();
}
